import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, X, Flame } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useCart } from '../App';
import { toast } from 'sonner';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, cartCount, updateQuantity, removeFromCart, clearCart, loading } = useCart();

  const handleRemoveItem = async (productId, productName) => {
    await removeFromCart(productId);
    toast.success(`${productName} removed from cart`);
  };

  const handleClearCart = async () => {
    await clearCart();
    toast.success('Cart cleared');
  };

  if (cartCount === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6" data-testid="empty-cart">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-stone-100 flex items-center justify-center">
            <ShoppingBag className="w-10 h-10 text-[#8C8C8C]" />
          </div>
          <h1 className="font-serif text-3xl text-[#2C2C2C] mb-4">Your Cart is Empty</h1>
          <p className="text-[#595959] mb-8">
            Looks like you haven't added any items to your cart yet. Explore our collection and find something you'll love!
          </p>
          <Link to="/shop">
            <Button className="btn-primary">
              Start Shopping
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="cart-page">
      {/* Header */}
      <div className="bg-[#5D6D53] py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-cursive text-2xl text-[#E5C88A] mb-2"
          >
            Your
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl text-white"
          >
            Shopping Cart
          </motion.h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Clear Cart Button */}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearCart}
                disabled={loading}
                className="text-[#B94A48] hover:text-[#B94A48] hover:bg-red-50"
                data-testid="clear-cart-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Cart
              </Button>
            </div>

            {cart.items.map((item, index) => (
              <motion.div
                key={item.product_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-stone-100 flex gap-6"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden bg-[#F5F1EB]">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C5A059] to-[#E5C88A] opacity-30" />
                      </div>
                    )}
                  </div>
                </Link>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product_id}`}>
                    <h3 className="font-serif text-lg text-[#2C2C2C] hover:text-[#C5A059] transition-colors truncate">
                      {item.name}
                    </h3>
                  </Link>
                  {item.fragrance && (
                    <p className="text-sm text-[#595959] flex items-center gap-1 mt-1">
                      <Flame className="w-3 h-3 text-[#C5A059]" />
                      {item.fragrance}
                    </p>
                  )}
                  <p className="text-[#C5A059] font-semibold mt-1">
                    £{item.price?.toFixed(2)}
                  </p>

                  {/* Quantity Controls */}
                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex items-center border border-stone-200 rounded-full overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        disabled={loading}
                        className="px-3 py-2 hover:bg-stone-100 transition-colors disabled:opacity-50"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="px-4 py-2 font-medium text-sm">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        disabled={loading}
                        className="px-3 py-2 hover:bg-stone-100 transition-colors disabled:opacity-50"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    <button
                      onClick={() => handleRemoveItem(item.product_id, item.name)}
                      disabled={loading}
                      className="text-[#8C8C8C] hover:text-[#B94A48] transition-colors disabled:opacity-50 p-2 hover:bg-red-50 rounded-full"
                      data-testid={`remove-${item.product_id}`}
                      title="Remove item"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Subtotal */}
                <div className="text-right hidden sm:block">
                  <p className="text-sm text-[#8C8C8C]">Subtotal</p>
                  <p className="font-serif text-lg text-[#2C2C2C]">
                    £{item.subtotal?.toFixed(2)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100 sticky top-28">
              <h2 className="font-serif text-2xl text-[#2C2C2C] mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#595959]">
                  <span>Subtotal ({cartCount} items)</span>
                  <span>£{cart.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#595959]">
                  <span>Postage & Packing</span>
                  <span>£4.00</span>
                </div>
              </div>

              <div className="border-t border-stone-200 pt-4 mb-6">
                <div className="flex justify-between text-lg font-semibold">
                  <span className="font-serif">Total</span>
                  <span className="text-[#C5A059]" data-testid="cart-total">£{(cart.total + 4).toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="btn-primary w-full"
                data-testid="checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <p className="text-center text-sm text-[#8C8C8C] mt-3">
                No account needed - checkout as guest
              </p>

              <Link to="/shop" className="block mt-4">
                <Button variant="ghost" className="w-full text-[#595959]">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
